import React, { useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Tooltip,
  Box,
  Typography
} from '@mui/material';
import { 
  ASSIGNMENT_MODEL_TYPES, 
  getAssignmentModelDescription,
  getAssignmentModelTypeName,
  getAssignmentModelTypeBackgroundColor,
  getAssignmentModelTypeTextColor
} from '../../../constants/assignmentModel';

const AssignmentModelSelector = ({ 
  value = '', 
  onChange, 
  label = "Assignment Model",
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  showEmptyOption = true,
  emptyOptionLabel = "None",
  showDescription = true,
  defaultValue = ASSIGNMENT_MODEL_TYPES.PLACEMENT,
  ...props 
}) => {
  // Set default value on mount if no value is provided
  useEffect(() => {
    if (!value && defaultValue && onChange) {
      onChange(defaultValue);
    }
  }, []);
  const assignmentModelOptions = Object.entries(ASSIGNMENT_MODEL_TYPES).map(([key, value]) => ({
    id: key,
    value: value,
    name: getAssignmentModelTypeName(value),
    description: getAssignmentModelDescription(value),
    backgroundColor: getAssignmentModelTypeBackgroundColor(value),
    textColor: getAssignmentModelTypeTextColor(value)
  }));

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    if (onChange) {
      onChange(selectedValue);
    }
  };

  const renderValue = (selected) => {
    if (!selected) return '';
    const option = assignmentModelOptions.find(opt => opt.value === selected);
    if (!option) return selected;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: option.backgroundColor,
            color: option.textColor,
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          {option.name}
        </Box>
      </Box>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled} size={size} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || defaultValue}
        onChange={handleChange}
        label={label}
        disabled={disabled}
        renderValue={renderValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350
            }
          }
        }}
      >
        {!required && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        {assignmentModelOptions.map(option => (
          <MenuItem key={option.id} value={option.value}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: option.backgroundColor,
                    color: option.textColor,
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {option.name}
                </Box>
              </Box>
              {showDescription && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    mt: 0.5,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    lineHeight: 1.4
                  }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default AssignmentModelSelector;